// frontend/src/pages/Reports/ReportsPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Stack,
  LinearProgress,
  TextField,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  ShoppingCart as SalesIcon,
  Inventory as InventoryIcon,
  People as CustomersIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  DateRange as DateRangeIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  CalendarToday as YearIcon,
  Timeline as CustomIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import './ReportsPage.css';

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'LKR 0.00';
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-LK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

// Simple Bar Chart Component
const SimpleBarChart = ({ data, dataKey = 'total_sales', nameKey = 'period' }) => {
  const safeData = React.useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object' && data !== null) {
      if (data.dataValues) return [data.dataValues];
      return [data];
    }
    return [];
  }, [data]);

  if (!safeData || safeData.length === 0) {
    return (
      <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="textSecondary">No trend data available</Typography>
      </Box>
    );
  }

  const maxValue = Math.max(...safeData.map(item => {
    const val = item[dataKey];
    return val ? parseFloat(val) : 0;
  }), 0.01);
  
  const chartHeight = 180;

  return (
    <Box sx={{ height: 250, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: chartHeight }}>
        {safeData.slice(0, 10).map((item, index) => {
          const value = item[dataKey] ? parseFloat(item[dataKey]) : 0;
          const barHeight = maxValue > 0 ? (value / maxValue) * chartHeight : 0;
          const label = item[nameKey] || '';
          
          return (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '8%' }}>
              <Tooltip title={formatCurrency(value)}>
                <Box 
                  sx={{ 
                    height: Math.max(barHeight, 4), 
                    width: '100%', 
                    bgcolor: COLORS[index % COLORS.length],
                    borderRadius: '4px 4px 0 0',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 }
                  }} 
                />
              </Tooltip>
              <Typography variant="caption" sx={{ mt: 1, transform: 'rotate(-45deg)', fontSize: '0.7rem' }}>
                {label ? (typeof label === 'string' ? label.slice(-5) : String(label).slice(-5)) : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Typography variant="caption" color="textSecondary">
          {safeData.length} periods shown
        </Typography>
      </Box>
    </Box>
  );
};

// Simple Pie Chart Component
const SimplePieChart = ({ data, valueKey = 'total_amount', nameKey = 'payment_method' }) => {
  const safeData = React.useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object' && data !== null) {
      if (data.dataValues) return [data.dataValues];
      return [data];
    }
    return [];
  }, [data]);

  if (!safeData || safeData.length === 0) {
    return (
      <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="textSecondary">No payment data available</Typography>
      </Box>
    );
  }

  const total = safeData.reduce((sum, item) => {
    const val = item[valueKey];
    return sum + (val ? parseFloat(val) : 0);
  }, 0);

  return (
    <Box sx={{ height: 250, overflow: 'auto', p: 1 }}>
      {safeData.map((item, index) => {
        const value = item[valueKey] ? parseFloat(item[valueKey]) : 0;
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        const name = item[nameKey] || 'Unknown';
        
        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ width: 16, height: 16, bgcolor: COLORS[index % COLORS.length], borderRadius: '50%', mr: 1 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                  {name}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formatCurrency(value)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LinearProgress 
                  variant="determinate" 
                  value={percentage} 
                  sx={{ 
                    flex: 1, 
                    height: 6, 
                    borderRadius: 3,
                    bgcolor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: COLORS[index % COLORS.length]
                    }
                  }} 
                />
                <Typography variant="caption" sx={{ ml: 1, minWidth: 40 }}>
                  {percentage}%
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const ReportsPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState({ csv: false, pdf: false });
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [profitPeriod, setProfitPeriod] = useState('monthly'); // daily, weekly, monthly, annual, custom
  const [profitAnchorEl, setProfitAnchorEl] = useState(null);
  const [profitData, setProfitData] = useState({
    daily: { revenue: 0, cost: 0, profit: 0, margin: 0 },
    weekly: { revenue: 0, cost: 0, profit: 0, margin: 0 },
    monthly: { revenue: 0, cost: 0, profit: 0, margin: 0 },
    annual: { revenue: 0, cost: 0, profit: 0, margin: 0 },
    custom: { revenue: 0, cost: 0, profit: 0, margin: 0 }
  });
  const [reportData, setReportData] = useState({
    sales: null,
    inventory: null,
    customers: null
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchReportData();
  }, [tabValue]);

  useEffect(() => {
    if (reportData.sales) {
      calculateProfitData();
    }
  }, [reportData.sales, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      console.log(`Fetching ${tabValue === 0 ? 'sales' : tabValue === 1 ? 'inventory' : 'customers'} report...`);
      
      switch (tabValue) {
        case 0:
          response = await apiService.getSalesReport({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate
          });
          break;
        case 1:
          response = await apiService.getInventoryReport();
          break;
        case 2:
          response = await apiService.getCustomerReport();
          break;
        default:
          return;
      }
      
      if (response && response.success) {
        console.log('Report data received:', response.data);
        
        // Ensure salesTrends is an array for sales report
        if (tabValue === 0 && response.data) {
          if (!Array.isArray(response.data.salesTrends)) {
            response.data.salesTrends = response.data.salesTrends ? [response.data.salesTrends] : [];
          }
        }
        
        setReportData(prev => ({
          ...prev,
          [tabValue === 0 ? 'sales' : tabValue === 1 ? 'inventory' : 'customers']: response.data
        }));
      } else {
        setError('Failed to load report data');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      setError(err.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const calculateProfitData = () => {
    // This is a simplified profit calculation
    // In a real system, you would fetch cost data from your database
    const salesData = reportData.sales;
    if (!salesData) return;

    const totalRevenue = salesData.summary?.netSales || 0;
    
    // Estimate cost as 70% of revenue (you should replace this with actual cost data)
    const estimatedCost = totalRevenue * 0.7;
    const estimatedProfit = totalRevenue - estimatedCost;
    const estimatedMargin = totalRevenue > 0 ? (estimatedProfit / totalRevenue) * 100 : 0;

    // Safely handle salesTrends - ensure it's an array
    const salesTrends = Array.isArray(salesData.salesTrends) ? salesData.salesTrends : [];
    
    // Daily profit (last 24 hours)
    const dailyRevenue = salesTrends.length > 0 
      ? salesTrends.slice(0, 1).reduce((sum, item) => sum + (item?.total_sales || 0), 0) 
      : 0;
    const dailyCost = dailyRevenue * 0.7;
    
    // Weekly profit (last 7 days)
    const weeklyRevenue = salesTrends.length > 0 
      ? salesTrends.slice(0, Math.min(7, salesTrends.length)).reduce((sum, item) => sum + (item?.total_sales || 0), 0) 
      : 0;
    const weeklyCost = weeklyRevenue * 0.7;
    
    // Monthly profit (last 30 days)
    const monthlyRevenue = salesTrends.length > 0 
      ? salesTrends.slice(0, Math.min(30, salesTrends.length)).reduce((sum, item) => sum + (item?.total_sales || 0), 0) 
      : totalRevenue;
    const monthlyCost = monthlyRevenue * 0.7;
    
    // Annual profit (last 365 days - estimate)
    const annualRevenue = totalRevenue * 12; // Rough estimate
    const annualCost = annualRevenue * 0.7;

    setProfitData({
      daily: {
        revenue: dailyRevenue,
        cost: dailyCost,
        profit: dailyRevenue - dailyCost,
        margin: dailyRevenue > 0 ? ((dailyRevenue - dailyCost) / dailyRevenue) * 100 : 0
      },
      weekly: {
        revenue: weeklyRevenue,
        cost: weeklyCost,
        profit: weeklyRevenue - weeklyCost,
        margin: weeklyRevenue > 0 ? ((weeklyRevenue - weeklyCost) / weeklyRevenue) * 100 : 0
      },
      monthly: {
        revenue: monthlyRevenue,
        cost: monthlyCost,
        profit: monthlyRevenue - monthlyCost,
        margin: monthlyRevenue > 0 ? ((monthlyRevenue - monthlyCost) / monthlyRevenue) * 100 : 0
      },
      annual: {
        revenue: annualRevenue,
        cost: annualCost,
        profit: annualRevenue - annualCost,
        margin: annualRevenue > 0 ? ((annualRevenue - annualCost) / annualRevenue) * 100 : 0
      },
      custom: {
        revenue: totalRevenue,
        cost: estimatedCost,
        profit: estimatedProfit,
        margin: estimatedMargin
      }
    });
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(prev => ({ ...prev, csv: true }));
      setError(null);
      
      let reportType = '';
      switch (tabValue) {
        case 0: reportType = 'sales'; break;
        case 1: reportType = 'inventory'; break;
        case 2: reportType = 'customers'; break;
        default: return;
      }
      
      console.log(`Exporting ${reportType} report to CSV...`);
      
      const response = await apiService.exportReportToCSV(reportType, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportType}_report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Unexpected response type:', typeof response);
        setError('Failed to export: Invalid response from server');
      }
    } catch (error) {
      console.error('Export error:', error);
      setError(error.message || 'Failed to export to CSV');
    } finally {
      setExportLoading(prev => ({ ...prev, csv: false }));
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportLoading(prev => ({ ...prev, pdf: true }));
      setError(null);
      
      let reportType = '';
      switch (tabValue) {
        case 0: reportType = 'sales'; break;
        case 1: reportType = 'inventory'; break;
        case 2: reportType = 'customers'; break;
        default: return;
      }
      
      console.log(`Exporting ${reportType} report to PDF...`);
      
      const response = await apiService.exportReportToPDF(reportType, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Unexpected response type:', typeof response);
        setError('Failed to export: Invalid response from server');
      }
    } catch (error) {
      console.error('PDF export error:', error);
      setError(error.message || 'Failed to export to PDF');
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleApplyFilter = () => {
    fetchReportData();
  };

  const handleProfitMenuOpen = (event) => {
    setProfitAnchorEl(event.currentTarget);
  };

  const handleProfitMenuClose = () => {
    setProfitAnchorEl(null);
  };

  const handleProfitPeriodChange = (period) => {
    setProfitPeriod(period);
    handleProfitMenuClose();
  };

  const getProfitPeriodIcon = (period) => {
    switch(period) {
      case 'daily': return <TodayIcon />;
      case 'weekly': return <WeekIcon />;
      case 'monthly': return <MonthIcon />;
      case 'annual': return <YearIcon />;
      case 'custom': return <CustomIcon />;
      default: return <MonthIcon />;
    }
  };

  const getProfitPeriodLabel = (period) => {
    switch(period) {
      case 'daily': return 'Daily Profit';
      case 'weekly': return 'Weekly Profit';
      case 'monthly': return 'Monthly Profit';
      case 'annual': return 'Annual Profit';
      case 'custom': return 'Custom Range Profit';
      default: return 'Monthly Profit';
    }
  };

  const renderProfitCards = () => {
    const currentProfit = profitData[profitPeriod];
    
    return (
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Profit Analysis
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={getProfitPeriodIcon(profitPeriod)}
              endIcon={<MoreVertIcon />}
              onClick={handleProfitMenuOpen}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              {getProfitPeriodLabel(profitPeriod)}
            </Button>
            <Menu
              anchorEl={profitAnchorEl}
              open={Boolean(profitAnchorEl)}
              onClose={handleProfitMenuClose}
            >
              <MenuItem onClick={() => handleProfitPeriodChange('daily')}>
                <TodayIcon sx={{ mr: 1 }} /> Daily Profit
              </MenuItem>
              <MenuItem onClick={() => handleProfitPeriodChange('weekly')}>
                <WeekIcon sx={{ mr: 1 }} /> Weekly Profit
              </MenuItem>
              <MenuItem onClick={() => handleProfitPeriodChange('monthly')}>
                <MonthIcon sx={{ mr: 1 }} /> Monthly Profit
              </MenuItem>
              <MenuItem onClick={() => handleProfitPeriodChange('annual')}>
                <YearIcon sx={{ mr: 1 }} /> Annual Profit
              </MenuItem>
              <MenuItem onClick={() => handleProfitPeriodChange('custom')}>
                <CustomIcon sx={{ mr: 1 }} /> Custom Range
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                  Revenue
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976d2' }}>
                  {formatCurrency(currentProfit.revenue)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total sales
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: '#fff3e0' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                  Cost
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#ed6c02' }}>
                  {formatCurrency(currentProfit.cost)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Estimated (70% of revenue)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: '#e8f5e8' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                  Profit
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                  {formatCurrency(currentProfit.profit)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Net profit
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: '#f3e5f5' }}>
              <CardContent>
                <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontWeight: 500 }}>
                  Profit Margin
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#7b1fa2' }}>
                  {currentProfit.margin.toFixed(1)}%
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={currentProfit.margin}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: '#e1bee7',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#7b1fa2'
                      }
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Mini profit trend for different periods */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Tooltip title={`Daily: ${formatCurrency(profitData.daily.profit)}`}>
            <Chip 
              icon={<TodayIcon />} 
              label={formatCurrency(profitData.daily.profit)} 
              size="small"
              color={profitData.daily.profit >= 0 ? 'success' : 'error'}
              variant="outlined"
              onClick={() => setProfitPeriod('daily')}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title={`Weekly: ${formatCurrency(profitData.weekly.profit)}`}>
            <Chip 
              icon={<WeekIcon />} 
              label={formatCurrency(profitData.weekly.profit)} 
              size="small"
              color={profitData.weekly.profit >= 0 ? 'success' : 'error'}
              variant="outlined"
              onClick={() => setProfitPeriod('weekly')}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title={`Monthly: ${formatCurrency(profitData.monthly.profit)}`}>
            <Chip 
              icon={<MonthIcon />} 
              label={formatCurrency(profitData.monthly.profit)} 
              size="small"
              color={profitData.monthly.profit >= 0 ? 'success' : 'error'}
              variant="outlined"
              onClick={() => setProfitPeriod('monthly')}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title={`Annual: ${formatCurrency(profitData.annual.profit)}`}>
            <Chip 
              icon={<YearIcon />} 
              label={formatCurrency(profitData.annual.profit)} 
              size="small"
              color={profitData.annual.profit >= 0 ? 'success' : 'error'}
              variant="outlined"
              onClick={() => setProfitPeriod('annual')}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      </Paper>
    );
  };

  const renderSalesReport = () => {
    if (!reportData.sales) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No sales data available for the selected period</Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />} 
            onClick={fetchReportData}
            sx={{ mt: 2 }}
          >
            Refresh Data
          </Button>
        </Box>
      );
    }

    const { summary, salesTrends, paymentBreakdown, topProducts, recentTransactions } = reportData.sales;

    // Calculate payment totals
    let creditTotal = 0, cashTotal = 0, cardTotal = 0, otherTotal = 0;
    
    if (paymentBreakdown && Array.isArray(paymentBreakdown)) {
      paymentBreakdown.forEach(p => {
        const amount = parseFloat(p.total_amount || 0);
        if (p.payment_method === 'credit') creditTotal = amount;
        else if (p.payment_method === 'cash') cashTotal = amount;
        else if (p.payment_method === 'card') cardTotal = amount;
        else otherTotal += amount;
      });
    }

    const paidAmount = cashTotal + cardTotal + otherTotal;
    const pendingAmount = creditTotal;

    return (
      <Box>
        {/* Profit Analysis Cards */}
        {renderProfitCards()}
        
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Net Sales
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976d2' }}>
                      {formatCurrency(summary?.netSales)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {summary?.totalTransactions} Transactions
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56 }}>
                    <SalesIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Average Transaction
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                      {formatCurrency(summary?.averageTransaction)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Per sale
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#2e7d32', width: 56, height: 56 }}>
                    <TrendingUpIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: paidAmount > 0 ? '#e8f5e8' : '#f5f5f5' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography gutterBottom variant="body2" sx={{ fontWeight: 500, color: paidAmount > 0 ? '#2e7d32' : 'textSecondary' }}>
                      Paid Payments
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: paidAmount > 0 ? '#2e7d32' : 'textSecondary' }}>
                      {formatCurrency(paidAmount)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: paidAmount > 0 ? '#2e7d32' : 'textSecondary' }}>
                      Cash/Card/Other
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: paidAmount > 0 ? '#2e7d32' : '#9e9e9e', width: 56, height: 56 }}>
                    <CheckCircleIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: pendingAmount > 0 ? '#ffebee' : '#f5f5f5' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography gutterBottom variant="body2" sx={{ fontWeight: 500, color: pendingAmount > 0 ? '#c62828' : 'textSecondary' }}>
                      Pending Payments
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: pendingAmount > 0 ? '#c62828' : 'textSecondary' }}>
                      {formatCurrency(pendingAmount)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: pendingAmount > 0 ? '#c62828' : 'textSecondary' }}>
                      Credit Sales
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: pendingAmount > 0 ? '#c62828' : '#9e9e9e', width: 56, height: 56 }}>
                    <PendingIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={8}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Sales Trend
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <SimpleBarChart data={salesTrends} dataKey="total_sales" nameKey="period" />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Payment Methods
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <SimplePieChart data={paymentBreakdown} valueKey="total_amount" nameKey="payment_method" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Top Products Table */}
        {topProducts && topProducts.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 2, mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Top Selling Products
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity Sold</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total Sales</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topProducts.map((product, index) => (
                      <TableRow key={product.product_id || index} hover>
                        <TableCell>{product.product_name}</TableCell>
                        <TableCell align="right">{product.total_quantity}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(product.total_sales)}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${summary?.netSales ? ((product.total_sales / summary.netSales) * 100).toFixed(1) : 0}%`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Recent Transactions Table */}
        {recentTransactions && recentTransactions.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Recent Transactions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTransactions
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((transaction, index) => (
                        <TableRow key={transaction.id || index} hover>
                          <TableCell>
                            <Chip
                              icon={<ReceiptIcon />}
                              label={transaction.pos_number || 'N/A'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{formatDate(transaction.transaction_date)}</TableCell>
                          <TableCell>{transaction.customer_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.payment_method || 'N/A'}
                              size="small"
                              color={
                                transaction.payment_method === 'cash' ? 'success' :
                                transaction.payment_method === 'card' ? 'primary' :
                                transaction.payment_method === 'credit' ? 'warning' : 'default'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.payment_status || 'N/A'}
                              size="small"
                              color={
                                transaction.payment_status === 'paid' ? 'success' :
                                transaction.payment_status === 'pending' ? 'warning' : 'error'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right" fontWeight="bold">
                            {formatCurrency(transaction.total_amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={recentTransactions.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  const renderInventoryReport = () => {
    if (!reportData.inventory) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No inventory data available</Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />} 
            onClick={fetchReportData}
            sx={{ mt: 2 }}
          >
            Refresh Data
          </Button>
        </Box>
      );
    }

    const { summary, inventory, category_breakdown } = reportData.inventory;

    const lowStockItems = inventory?.filter(i => i.stock_status === 'low_stock') || [];
    const outOfStockItems = inventory?.filter(i => i.stock_status === 'out_of_stock') || [];

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Total Items
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976d2' }}>
                      {summary?.total_items || 0}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56 }}>
                    <InventoryIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Total Value
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                      {formatCurrency(summary?.total_value)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#2e7d32', width: 56, height: 56 }}>
                    <TrendingUpIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: lowStockItems.length > 0 ? '#fff3e0' : '#f5f5f5' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography gutterBottom variant="body2" sx={{ fontWeight: 500, color: lowStockItems.length > 0 ? '#ed6c02' : 'textSecondary' }}>
                      Low Stock Items
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: lowStockItems.length > 0 ? '#ed6c02' : 'textSecondary' }}>
                      {lowStockItems.length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: lowStockItems.length > 0 ? '#ed6c02' : '#9e9e9e', width: 56, height: 56 }}>
                    <WarningIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: outOfStockItems.length > 0 ? '#ffebee' : '#f5f5f5' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography gutterBottom variant="body2" sx={{ fontWeight: 500, color: outOfStockItems.length > 0 ? '#c62828' : 'textSecondary' }}>
                      Out of Stock
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: outOfStockItems.length > 0 ? '#c62828' : 'textSecondary' }}>
                      {outOfStockItems.length}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: outOfStockItems.length > 0 ? '#c62828' : '#9e9e9e', width: 56, height: 56 }}>
                    <TrendingDownIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Category Breakdown */}
        {category_breakdown && category_breakdown.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 2, mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Category Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Items</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>% of Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {category_breakdown.map((category, index) => (
                      <TableRow key={index} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{category.name}</TableCell>
                        <TableCell align="right">{category.count}</TableCell>
                        <TableCell align="right">{category.quantity}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(category.value)}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${category.percentage_of_total?.toFixed(1)}%`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Inventory Table */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Inventory Items
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={item.id || index} hover>
                        <TableCell>
                          <Chip label={item.product_code || 'N/A'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.price)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(item.inventory_value)}</TableCell>
                        <TableCell>
                          <Chip
                            label={item.stock_status?.replace('_', ' ') || 'unknown'}
                            size="small"
                            color={
                              item.stock_status === 'adequate' ? 'success' :
                              item.stock_status === 'low_stock' ? 'warning' : 'error'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={inventory?.length || 0}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderCustomerReport = () => {
    if (!reportData.customers) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">No customer data available</Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />} 
            onClick={fetchReportData}
            sx={{ mt: 2 }}
          >
            Refresh Data
          </Button>
        </Box>
      );
    }

    const { summary, customers } = reportData.customers;

    const creditCustomers = customers?.filter(c => c.is_credit_customer) || [];
    const customersWithOutstanding = customers?.filter(c => parseFloat(c.outstanding_balance) > 0) || [];
    const totalOutstanding = customersWithOutstanding.reduce((sum, c) => sum + parseFloat(c.outstanding_balance), 0);

    return (
      <Box>
        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Total Customers
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976d2' }}>
                      {summary?.total_customers || 0}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#1976d2', width: 56, height: 56 }}>
                    <CustomersIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Total Spent
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                      {formatCurrency(summary?.total_spent)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#2e7d32', width: 56, height: 56 }}>
                    <TrendingUpIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2" sx={{ fontWeight: 500 }}>
                      Credit Customers
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#ed6c02' }}>
                      {summary?.credit_customers || 0}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: '#ed6c02', width: 56, height: 56 }}>
                    <PaymentIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ borderRadius: 2, bgcolor: totalOutstanding > 0 ? '#ffebee' : '#f5f5f5' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography gutterBottom variant="body2" sx={{ fontWeight: 500, color: totalOutstanding > 0 ? '#c62828' : 'textSecondary' }}>
                      Total Outstanding
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: totalOutstanding > 0 ? '#c62828' : 'textSecondary' }}>
                      {formatCurrency(totalOutstanding)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: totalOutstanding > 0 ? '#c62828' : 'textSecondary' }}>
                      {customersWithOutstanding.length} customers
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: totalOutstanding > 0 ? '#c62828' : '#9e9e9e', width: 56, height: 56 }}>
                    <PendingIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Segment Breakdown */}
        {summary?.segment_breakdown && Object.keys(summary.segment_breakdown).length > 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Customer Segments
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Segment</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(summary.segment_breakdown).map(([segment, count], index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{segment}</TableCell>
                            <TableCell align="right">{count}</TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={`${((count / summary.total_customers) * 100).toFixed(1)}%`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Top Customers by Spending
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total Spent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customers
                          ?.sort((a, b) => b.total_spent - a.total_spent)
                          .slice(0, 5)
                          .map((customer, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{customer.customer_name}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(customer.total_spent)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Credit Customers Table */}
        {creditCustomers.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 2, mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Credit Customers
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Credit Limit</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Outstanding</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Available</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Utilization</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {creditCustomers
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((customer, index) => {
                        const utilization = customer.credit_limit > 0 
                          ? (customer.outstanding_balance / customer.credit_limit) * 100 
                          : 0;
                        return (
                          <TableRow key={customer.customer_id || index} hover>
                            <TableCell>
                              <Chip label={customer.customer_code || 'N/A'} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{customer.customer_name}</TableCell>
                            <TableCell align="right">{formatCurrency(customer.credit_limit)}</TableCell>
                            <TableCell align="right" sx={{ color: customer.outstanding_balance > 0 ? '#c62828' : 'inherit', fontWeight: customer.outstanding_balance > 0 ? 600 : 400 }}>
                              {formatCurrency(customer.outstanding_balance)}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(customer.credit_limit - customer.outstanding_balance)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={utilization}
                                  sx={{
                                    width: 80,
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: utilization > 80 ? '#ffebee' : '#e0e0e0',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: utilization > 80 ? '#c62828' : '#1976d2'
                                    }
                                  }}
                                />
                                <Typography variant="caption">
                                  {utilization.toFixed(1)}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* All Customers Table */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              All Customers
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Purchases</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total Spent</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Outstanding</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Segment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customers
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((customer, index) => (
                      <TableRow key={customer.customer_id || index} hover>
                        <TableCell>
                          <Chip label={customer.customer_code || 'N/A'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{customer.customer_name}</TableCell>
                        <TableCell>
                          <Chip
                            label={customer.is_credit_customer ? 'Credit' : 'Cash'}
                            size="small"
                            color={customer.is_credit_customer ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{customer.total_purchases}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(customer.total_spent)}</TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            color: customer.outstanding_balance > 0 ? '#c62828' : 'inherit',
                            fontWeight: customer.outstanding_balance > 0 ? 600 : 400
                          }}
                        >
                          {formatCurrency(customer.outstanding_balance)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={customer.customer_segment || 'New'}
                            size="small"
                            color={
                              customer.customer_segment === 'Champion' ? 'success' :
                              customer.customer_segment === 'Loyal' ? 'primary' :
                              customer.customer_segment === 'At Risk' ? 'warning' : 'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={customers?.length || 0}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Header with Title and Export Buttons */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976d2' }}>
              Reports & Analytics
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Generate and download comprehensive business reports
            </Typography>
          </Grid>
          <Grid item>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CsvIcon />}
                onClick={handleExportCSV}
                disabled={loading || exportLoading.csv || !reportData}
                sx={{ borderRadius: 2 }}
              >
                {exportLoading.csv ? <CircularProgress size={24} color="inherit" /> : 'CSV Export'}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<PdfIcon />}
                onClick={handleExportPDF}
                disabled={loading || exportLoading.pdf || !reportData}
                sx={{ borderRadius: 2 }}
              >
                {exportLoading.pdf ? <CircularProgress size={24} color="inherit" /> : 'PDF Export'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => window.print()}
                sx={{ borderRadius: 2 }}
              >
                Print
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Date Range Filter - Only show for Sales tab */}
        {tabValue === 0 && (
          <Paper elevation={1} sx={{ p: 2, mt: 3, borderRadius: 2, bgcolor: 'white' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="End Date"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: 'white' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<FilterIcon />}
                  onClick={handleApplyFilter}
                  sx={{ height: '40px', borderRadius: 2 }}
                >
                  Apply Filter
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Paper>

      {/* Tabs */}
      <Paper elevation={1} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ bgcolor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<SalesIcon />} 
            label="POS SALES" 
            iconPosition="start"
            sx={{ fontWeight: 600 }}
          />
          <Tab 
            icon={<InventoryIcon />} 
            label="INVENTORY" 
            iconPosition="start"
            sx={{ fontWeight: 600 }}
          />
          <Tab 
            icon={<CustomersIcon />} 
            label="CUSTOMERS" 
            iconPosition="start"
            sx={{ fontWeight: 600 }}
          />
        </Tabs>

        {/* Loading Indicator */}
        {loading && (
          <Box sx={{ width: '100%', p: 5, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
          </Box>
        )}

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ m: 3, borderRadius: 2 }} 
            onClose={() => setError(null)}
            action={
              <Button color="inherit" size="small" onClick={fetchReportData}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Tab Panels */}
        {!loading && !error && (
          <>
            <TabPanel value={tabValue} index={0}>
              {renderSalesReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderInventoryReport()}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {renderCustomerReport()}
            </TabPanel>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ReportsPage;